import { useState } from 'react';
import { Stack, Text, Textarea, Button, Chip, Group, Center, Loader, Alert } from '@mantine/core';
import { IconSend, IconAlertCircle } from '@tabler/icons-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getFeedbackCategories, submitFeedback } from '@/client/sdk.gen';
import { useStore } from '@/store';
import { translate, showToast, sanitizeInput } from '@/lib';

interface ReportFormProps {
  itemId: number;
  onSuccess: () => void;
}

export function ReportForm({ itemId, onSuccess }: ReportFormProps) {
  const { isAniming, updateState } = useStore();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [content, setContent] = useState('');

  const { data: categories, isLoading: isCategoriesLoading } = useQuery({
    queryKey: ['feedbackCategories'],
    queryFn: async () => {
      const { data } = await getFeedbackCategories({ throwOnError: true });
      return data;
    },
  });

  const { mutate: submit } = useMutation({
    mutationFn: async () => {
      const { data } = await submitFeedback({
        body: {
          itemId,
          categories: selectedCategories.map(Number),
          content: content.trim() ? sanitizeInput(content.trim()) : undefined,
        },
        throwOnError: true,
      });
      return data;
    },
    onMutate: () => {
      updateState('isAniming', true);
    },
    onSuccess: () => {
      updateState('isAniming', false);
      showToast(translate('ff.report.submitSuccess', 'Report submitted successfully'));
      onSuccess();
    },
    onError: () => {
      updateState('isAniming', false);
      showToast(translate('ff.report.submitError', 'Failed to submit report'), 0, 'red');
    },
  });

  const hasSelectedCategory = selectedCategories.length > 0;

  const handleSubmit = () => {
    if (!hasSelectedCategory || isAniming) return;
    submit();
  };

  if (isCategoriesLoading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <Alert variant="light" color="orange" icon={<IconAlertCircle size={18} />}>
        <Text size="sm">
          {translate('ff.report.description', 'Please select the reason(s) for reporting this item.')}
        </Text>
      </Alert>

      <div>
        <Text fw={600} size="sm" mb="xs">
          {translate('ff.report.categoryLabel', 'Report reason')} <Text component="span" c="red" inherit>*</Text>
        </Text>
        <Chip.Group multiple value={selectedCategories} onChange={(values) => {
          if (values.length > 3) {
            showToast(translate('ff.report.categoryLimit', 'You can select up to 3 reasons'), 2000, 'orange');
            return;
          }
          setSelectedCategories(values);
        }}>
          <Group gap="xs">
            {categories?.map((category) => (
              <Chip key={category.id} value={String(category.id)} size="sm" variant="outline">
                {category.transify ? translate(category.transify, category.name) : category.name}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </div>

      <Textarea
        label={translate('ff.report.contentLabel', 'Additional details')}
        placeholder={translate('ff.report.contentPlaceholder', 'Describe the issue in more detail (optional)...')}
        value={content}
        onChange={(e) => setContent(e.currentTarget.value)}
        minRows={3}
        maxRows={5}
        maxLength={500}
        autosize
      />

      <Button
        onClick={handleSubmit}
        disabled={!hasSelectedCategory}
        loading={isAniming}
        leftSection={<IconSend size={16} />}
        color="orange"
        fullWidth
      >
        {translate('ff.report.submit', 'Submit Report')}
      </Button>
    </Stack>
  );
}
